const SELECTORS = Object.freeze({
  checkout: '[data-vorliva-checkout-control]',
  addressAssist: '[data-addressly-assisted-checkout]',
  addressStatus: '[data-address-assist-status]',
  addressOverlay: '#addressly-modal-overlay',
  addressModal: '#addressly-modal',
  addressSearch: '#addressly-search-input',
  addressClose: '#addressly-modal-close',
});

const CHECKOUT_PENDING_TIMEOUT = 10000;
const CHECKOUT_RECOVERY_TIMEOUT = 2500;
const ADDRESS_ASSIST_CHECKS = [100, 650, 1600];
const AUTO_ADDRESS_CONTROL_TIMEOUT = 5000;
const AUTO_ADDRESS_VERIFY_DELAY = 2400;

const pendingCartMutations = new Set();
const state = {
  checkoutInFlight: false,
  checkoutRecoveryTimer: undefined,
  addressAssistRequested: false,
  addressAssistAutomatic: false,
  autoAddressAssistInFlight: false,
  addressAssistTimers: new Set(),
};

const cartMutations = {
  /** @param {Promise<any>} promise */
  track(promise) {
    const tracked = Promise.resolve(promise);
    pendingCartMutations.add(tracked);
    tracked.finally(() => pendingCartMutations.delete(tracked)).catch(() => {});
    return promise;
  },

  hasPending() {
    return pendingCartMutations.size > 0;
  },

  /** @param {number} [timeoutMs] */
  async waitForIdle(timeoutMs = CHECKOUT_PENDING_TIMEOUT) {
    const deadline = Date.now() + timeoutMs;
    while (pendingCartMutations.size > 0) {
      const remaining = deadline - Date.now();
      if (remaining <= 0) throw new Error('Cart update is taking too long. Please try again.');

      let timeoutId;
      try {
        await Promise.race([
          Promise.allSettled(Array.from(pendingCartMutations)),
          new Promise((_, reject) => {
            timeoutId = window.setTimeout(
              () => reject(new Error('Cart update is taking too long. Please try again.')),
              remaining
            );
          }),
        ]);
      } finally {
        if (timeoutId) window.clearTimeout(timeoutId);
      }
    }
  },
};

window.VorlivaCartMutations = cartMutations;

function getLiveCheckoutControl() {
  return Array.from(document.querySelectorAll(SELECTORS.checkout)).find(
    (control) =>
      control instanceof HTMLButtonElement &&
      !control.disabled &&
      control.getAttribute('aria-disabled') !== 'true' &&
      control.dataset.vorlivaCartEmpty !== 'true'
  );
}

function getLiveAddressControl() {
  return Array.from(document.querySelectorAll(SELECTORS.addressAssist)).find(
    (control) =>
      control instanceof HTMLButtonElement &&
      !control.disabled &&
      control.getAttribute('aria-disabled') !== 'true'
  );
}

function setCheckoutBusy(control, busy) {
  if (!(control instanceof HTMLButtonElement)) return;
  if (busy) control.setAttribute('aria-busy', 'true');
  else control.removeAttribute('aria-busy');
  if (control.dataset.vorlivaCartEmpty === 'true') return;
  control.disabled = busy;
  control.setAttribute('aria-disabled', String(busy));
  if (!busy) control.removeAttribute('aria-disabled');
}

function resetCheckout() {
  state.checkoutInFlight = false;
  if (state.checkoutRecoveryTimer) window.clearTimeout(state.checkoutRecoveryTimer);
  state.checkoutRecoveryTimer = undefined;
  for (const control of document.querySelectorAll(SELECTORS.checkout)) setCheckoutBusy(control, false);
}

function startNativeCheckout() {
  const root = window.Shopify?.routes?.root || '/';
  const checkoutUrl = new URL(`${root}checkout`, window.location.origin).href;
  window.location.assign(checkoutUrl);
}

function showGlobalCheckoutError(message) {
  const liveRegion = document.querySelector('header-actions [ref="liveRegion"]');
  if (liveRegion) liveRegion.textContent = message;
}

async function handleNativeCheckout(event, control) {
  event.preventDefault();
  event.stopImmediatePropagation();
  if (state.checkoutInFlight || control.disabled || control.getAttribute('aria-disabled') === 'true') return;

  state.checkoutInFlight = true;
  setCheckoutBusy(control, true);
  try {
    if (cartMutations.hasPending()) await cartMutations.waitForIdle();
    startNativeCheckout();
    state.checkoutRecoveryTimer = window.setTimeout(resetCheckout, CHECKOUT_RECOVERY_TIMEOUT);
  } catch (error) {
    resetCheckout();
    showGlobalCheckoutError(error instanceof Error ? error.message : 'Checkout could not start. Please try again.');
  }
}

function getAddressStatus(control) {
  const scope = control.closest('.cart-address-assist');
  return scope?.querySelector(SELECTORS.addressStatus) || document.querySelector(SELECTORS.addressStatus);
}

function setAddressStatus(control, message, isError = false) {
  const status = getAddressStatus(control);
  if (!status) return;
  status.textContent = message;
  status.toggleAttribute('data-error', isError);
}

function closeCartSurfaceForAddressAssist(control) {
  const dialog = control.closest('dialog[open]');
  if (!(dialog instanceof HTMLDialogElement)) return;

  const drawer = /** @type {(HTMLElement & {close?: () => void}) | null} */ (dialog.closest('theme-drawer'));
  if (drawer?.close) {
    drawer.close();
    return;
  }

  const closeButton = dialog.querySelector('.theme-drawer__close-button');
  if (closeButton instanceof HTMLButtonElement) closeButton.click();
  else dialog.close();
}

function clearAddressTimers() {
  for (const timer of state.addressAssistTimers) window.clearTimeout(timer);
  state.addressAssistTimers.clear();
}

function openCartFallback(message) {
  const drawer = /** @type {(HTMLElement & {open?: () => void}) | null} */ (
    document.querySelector('theme-drawer#cart-drawer')
  );
  drawer?.open?.();

  const control = getLiveAddressControl();
  if (control) setAddressStatus(control, message, true);
}

function closeAddressAssist({ restoreCart = false } = {}) {
  clearAddressTimers();
  state.addressAssistRequested = false;
  state.addressAssistAutomatic = false;
  const overlay = document.querySelector(SELECTORS.addressOverlay);
  overlay?.classList.add('addressly-hidden');
  overlay?.removeAttribute('data-vorliva-recovered');
  overlay?.style.removeProperty('display');
  overlay?.style.removeProperty('visibility');
  overlay?.style.removeProperty('opacity');
  document.body.style.removeProperty('overflow');

  if (restoreCart) {
    window.setTimeout(
      () => openCartFallback('Your item is saved. You can try the address helper again or continue to secure checkout.'),
      120
    );
  }
}

function revealAddressAssist(control) {
  if (!state.addressAssistRequested) return;
  const overlay = document.querySelector(SELECTORS.addressOverlay);
  const modal = document.querySelector(SELECTORS.addressModal);
  if (!(overlay instanceof HTMLElement) || !(modal instanceof HTMLElement)) {
    document.body.style.removeProperty('overflow');
    setAddressStatus(control, 'Address helper is unavailable. Continue to secure checkout and enter it manually.', true);
    return;
  }

  const isHidden = overlay.classList.contains('addressly-hidden') || window.getComputedStyle(overlay).display === 'none';
  if (!isHidden) return;

  overlay.classList.remove('addressly-hidden');
  overlay.setAttribute('data-vorliva-recovered', 'true');
  overlay.style.setProperty('display', 'grid', 'important');
  overlay.style.setProperty('visibility', 'visible', 'important');
  overlay.style.setProperty('opacity', '1', 'important');
  modal.style.setProperty('visibility', 'visible', 'important');
  modal.style.setProperty('opacity', '1', 'important');
  modal.style.setProperty('transform', 'none', 'important');
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'addressly-modal-title');
  document.body.style.overflow = 'hidden';

  const close = document.querySelector(SELECTORS.addressClose);
  if (close && !close.getAttribute('aria-label')) close.setAttribute('aria-label', 'Close address helper');
  setAddressStatus(control, 'Search for your delivery address or use your current location.');
  window.setTimeout(() => document.querySelector(SELECTORS.addressSearch)?.focus(), 50);
}

function requestAddressAssist(control, { automatic = false } = {}) {
  state.addressAssistRequested = true;
  state.addressAssistAutomatic = automatic;
  setAddressStatus(control, 'Opening address helper…');
  clearAddressTimers();
  for (const delay of ADDRESS_ASSIST_CHECKS) {
    const timer = window.setTimeout(() => {
      state.addressAssistTimers.delete(timer);
      revealAddressAssist(control);
    }, delay);
    state.addressAssistTimers.add(timer);
  }
}

function isAddressAssistVisible() {
  const overlay = document.querySelector(SELECTORS.addressOverlay);
  if (!(overlay instanceof HTMLElement) || overlay.classList.contains('addressly-hidden')) return false;

  const styles = window.getComputedStyle(overlay);
  return styles.display !== 'none' && styles.visibility !== 'hidden' && Number(styles.opacity || 1) > 0;
}

async function waitForLiveAddressControl(timeoutMs = AUTO_ADDRESS_CONTROL_TIMEOUT) {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const control = getLiveAddressControl();
    if (control) return control;
    await new Promise((resolve) => window.setTimeout(resolve, 80));
  }

  return null;
}

async function openAddressAssistAfterCartAdd(event) {
  if (event.detail?.autoOpenAddressAssist !== true || state.autoAddressAssistInFlight) return;

  state.autoAddressAssistInFlight = true;
  try {
    if (cartMutations.hasPending()) await cartMutations.waitForIdle();

    const control = await waitForLiveAddressControl();
    if (!control) {
      openCartFallback('Your item was added, but the address helper is unavailable. Continue to checkout and enter it manually.');
      return;
    }

    // Addressly owns the actual address search and confirmation. Trigger its
    // existing button only after Shopify has rendered the non-empty cart, then
    // close the cart dialog in the captured click handler before the app opens.
    requestAddressAssist(control, { automatic: true });
    control.click();

    window.setTimeout(() => {
      if (!state.addressAssistAutomatic || isAddressAssistVisible()) return;

      closeAddressAssist();
      openCartFallback(
        'Your item was added, but the address helper could not open. Accept the cookie notice, then try again or continue to checkout.'
      );
    }, AUTO_ADDRESS_VERIFY_DELAY);
  } catch (error) {
    closeAddressAssist();
    openCartFallback(
      error instanceof Error
        ? error.message
        : 'Your item was added, but the address helper could not open. Continue to checkout and enter it manually.'
    );
  } finally {
    state.autoAddressAssistInFlight = false;
  }
}

function findControl(event, selector) {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  for (const node of path) {
    if (node instanceof Element && node.matches(selector)) return node;
  }
  return event.target instanceof Element ? event.target.closest(selector) : null;
}

document.addEventListener(
  'pointerdown',
  (event) => {
    const control = findControl(event, SELECTORS.addressAssist);
    if (!(control instanceof HTMLButtonElement) || control.disabled) return;
    requestAddressAssist(control);
  },
  { capture: true, passive: true }
);

document.addEventListener(
  'click',
  (event) => {
    const checkoutControl = findControl(event, SELECTORS.checkout);
    if (checkoutControl instanceof HTMLButtonElement) {
      void handleNativeCheckout(event, checkoutControl);
      return;
    }

    const addressControl = findControl(event, SELECTORS.addressAssist);
    if (addressControl instanceof HTMLButtonElement) {
      // Native <dialog> elements render in the browser top layer. Close the cart
      // drawer before Addressly opens so its fixed overlay cannot be hidden behind it.
      closeCartSurfaceForAddressAssist(addressControl);
      if (!state.addressAssistRequested) requestAddressAssist(addressControl);
    }

    if (event.target instanceof Element && event.target.closest(SELECTORS.addressClose)) {
      const restoreCart = state.addressAssistAutomatic;
      window.setTimeout(() => closeAddressAssist({ restoreCart }), 0);
    }
  },
  { capture: true }
);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const overlay = document.querySelector(SELECTORS.addressOverlay);
  if (overlay && !overlay.classList.contains('addressly-hidden')) {
    closeAddressAssist({ restoreCart: state.addressAssistAutomatic });
  }
});

document.addEventListener('vorliva:cart-item-added', (event) => {
  void openAddressAssistAfterCartAdd(event);
});

window.addEventListener('pagehide', () => {
  pendingCartMutations.clear();
  resetCheckout();
  closeAddressAssist();
});
window.addEventListener('pageshow', resetCheckout);
